import logo from "./logo.svg";
import "./App.css";
import { BrowserRouter, HashRouter, Route, Routes } from "react-router-dom";
import React, { Component, Suspense, useContext, useEffect } from "react";
import ScrollToTop from "./ScrollToTop";
import Footer from "./Views/Footer";

import AOS from "aos";
import "aos/dist/aos.css";
import { API_URL, UserContext, UserProvider } from "./utils";
import axios from "axios";

// Import i18n configuration
import "./i18n";
import { useTranslation } from "react-i18next";

import { TranslationProvider } from "./context/TranslationContext";
import LanguageSelector, { LanguageButton } from "./Translate/LanguageSelector";
import TranslatableText from "./Translate/TranslatedText";
import CookieConsent from "./components/CookieConsent";
import AccessibilityWidget from "./components/Accessibility/AccessibilityWidget";

// Import ProtectedRoute
import ProtectedRoute from "./ProtectedRoute";

// Lazy-loaded components
const Header = React.lazy(() => import("./Views/Header"));
const Home = React.lazy(() => import("./Views/Home/Home"));
const AboutUs = React.lazy(() => import("./Views/AboutUs/AboutUs"));
const Excursion = React.lazy(() => import("./Views/Excursions/Excursion"));
const Tour = React.lazy(() => import("./Views/Tour/Tour"));
const Destination = React.lazy(
  () => import("../src/destinations/destinations"),
);
const DestinationListings = React.lazy(
  () => import("../src/destinations/DestinationListings"),
);

const Transport = React.lazy(() => import("./Views/Transport/Transport"));
const TransportDetail = React.lazy(
  () => import("./Views/Transport/TransportDetail"),
);
const TransportBooking = React.lazy(
  () => import("./Views/Transport/TransportBooking"),
);
const TransportPayment = React.lazy(
  () => import("./Views/Transport/TransportPayment"),
);
const Booking = React.lazy(() => import("./Views/Excursions/Booking"));
const BookingSuccess = React.lazy(
  () => import("./Views/Excursions/BookingSuccess"),
);
const TourBookingSuccess = React.lazy(
  () => import("./Views/Tour/TourBookingSuccess"),
);
const ExcursionCityDetail = React.lazy(
  () => import("./Views/Excursions/ExcursionCityDetail"),
);
const BookingCancel = React.lazy(
  () => import("./Views/Excursions/BookingCancel"),
);
const SignUp = React.lazy(() => import("./Views/Signup/SignUp"));
const Hotel = React.lazy(() => import("./Views/Hotel/Hotel"));
const HotelDetail = React.lazy(() => import("./Views/Hotel/HotelDetail"));
const HotelBookingPage = React.lazy(() => import("./Views/Hotel/HotelBooking"));
const HotelPayment = React.lazy(() => import("./Views/Hotel/HotelPayment"));
const Contactus = React.lazy(() => import("./Views/ContactUs/ContactUs"));
const ContactEnquiry = React.lazy(
  () => import("./Views/ContactUs/ContactEnquiry"),
);
const TermsAndConditions = React.lazy(() => import("./Views/Terms/Terms"));
const PrivacyPolicy = React.lazy(() => import("./Views/Privacy/privacy"));
const PageNotFound = React.lazy(() => import("./Views/PageNotFound"));
const UserProfile = React.lazy(() => import("./Views/UserProfile/UserProfile"));
const ThankYou = React.lazy(() => import("./Views/ThankYou"));

// testing translate component
const TranslationTest = React.lazy(
  () => import("../src/Translate/TranslationTest"),
);

function App() {
  const { t } = useTranslation();

  useEffect(() => {
    AOS.init({
      duration: 100,
      once: true,
    });
    AOS.refresh();
  }, []);

  const { setDetails } = useContext(UserContext);
  const token = localStorage.getItem("token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const getUserDetails = async () => {
    try {
      const lang = localStorage.getItem("appLanguage") || "en";
      const response = await axios.get(
        `${API_URL}/get-user-details?lng=${lang}&language=${lang}`,
        config,
      );
      if (response.status === 200) {
        setDetails({
          name: response.data.user.name,
          userId: response.data.user.id,
          user_type: response.data.user.user_type,
          dob: response.data.user.dob,
          address: response.data.user.address,
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        console.warn("Token expired, logging out...");
        localStorage.removeItem("token");
        window.location.href = "/sign-up";
      } else {
        console.error("Failed to fetch user details:", error.message);
      }
    }
  };

  useEffect(() => {
    if (token) {
      getUserDetails();
    }
  }, []);

  return (
    <TranslationProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense
          fallback={
            <div className="pt-3 text-center">{/* Loading indicator */}</div>
          }
        >
          <Header />
          <Routes>
            {/* Public Routes */}
            <Route path="/" name="Home" Component={Home} />
            <Route path="/about-us" name="Who we are" Component={AboutUs} />
            <Route path="/excursion*" name="Excursion" Component={Excursion} />
            <Route path="/tour*" name="Tour" Component={Tour} />
            <Route path="/destinations" name="Tour" Component={Destination} />
            <Route
              path="/destinations-list"
              name="Tour"
              Component={DestinationListings}
            />

            <Route path="/transport" element={<Transport />} />
            <Route
              path="/transport/transport-details/:location_id"
              element={<TransportDetail />}
            />

            <Route path="/sign-up*" name="Signup" Component={SignUp} />
            <Route path="/hotel*" name="Hotel" Component={Hotel} />
            <Route
              path="/hotel/:hotelId/booking"
              element={<HotelBookingPage />}
            />
            <Route
              path="/hotel/:hotelId/booking/payment"
              element={<HotelPayment />}
            />

            <Route path="/contact-us" name="Contactus" Component={Contactus} />
            <Route
              path="/translation-test"
              name="Contactus"
              Component={TranslationTest}
            />

            <Route
              path="/terms-and-conditions"
              name="Terms"
              Component={TermsAndConditions}
            />
            <Route
              path="/privacy-policy"
              name="PrivacyPolicy"
              Component={PrivacyPolicy}
            />
            <Route path="/thank-you" element={<ThankYou />} />

            {/* Protected Routes */}
            <Route
              path="/booking/*"
              // path="/transport-booking*"
              element={
                <ProtectedRoute>
                  <Booking />
                  {/* <TransportBooking/> */}
                </ProtectedRoute>
              }
            />
            <Route
              path="/transport-booking*"
              element={
                <ProtectedRoute>
                  <TransportBooking />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hotel/:hotelId/booking"
              element={
                <ProtectedRoute>
                  <HotelBookingPage />
                </ProtectedRoute>
              }
            />
            <Route path="/booking-success" element={<BookingSuccess />} />
            <Route path="/booking-cancel" element={<BookingCancel />} />
            <Route path="/contact-enquiry" element={<ContactEnquiry />} />
            <Route
              path="/tour-booking-success"
              element={<TourBookingSuccess />}
            />

            <Route
              path="/user-profile"
              element={
                <ProtectedRoute>
                  <UserProfile />
                </ProtectedRoute>
              }
            />

            {/* 404 Route */}
            <Route path="*" element={<PageNotFound />} />
          </Routes>
          <AccessibilityWidget />
          <CookieConsent />
          <Footer />
        </Suspense>
      </BrowserRouter>
    </TranslationProvider>
  );
}

export default App;
